import { useEffect, useState } from 'react'
import { Pencil, X } from 'lucide-react'
import {
  confirmEmailChange,
  requestEmailChange,
  updateAccountName,
  updateAccountPassword,
} from '../../services/account'
import { validateEmail } from '../../utils/emailValidation'

type EditingField = null | 'name' | 'email' | 'password'

const inputClass =
  'w-full border border-rule rounded-sm bg-paper px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-300 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/10'
const labelClass =
  'block text-[13px] font-semibold text-ink-500 mb-0.5'
const formStackClass = 'space-y-2.5'

interface AccountSettingsFieldsProps {
  name: string
  email: string
  organizationName: string
  onNameSaved: () => Promise<void>
  onEmailSaved: () => Promise<void>
  onPasswordSaved: () => void
}

/**
 * Inline-editable account fields (name, e-mail, password).
 */
const AccountSettingsFields: React.FC<AccountSettingsFieldsProps> = ({
  name,
  email,
  organizationName,
  onNameSaved,
  onEmailSaved,
  onPasswordSaved,
}) => {
  const [editing, setEditing] = useState<EditingField>(null)

  return (
    <dl className="divide-y divide-rule border-y border-rule">
      <NameRow
        value={name}
        editing={editing === 'name'}
        onStart={() => setEditing('name')}
        onCancel={() => setEditing(null)}
        onSaved={async () => {
          await onNameSaved()
          setEditing(null)
        }}
      />
      <ReadOnlyRow label="Organisation" value={organizationName} />
      <EmailRow
        value={email}
        editing={editing === 'email'}
        onStart={() => setEditing('email')}
        onCancel={() => setEditing(null)}
        onSaved={async () => {
          await onEmailSaved()
          setEditing(null)
        }}
      />
      <PasswordRow
        editing={editing === 'password'}
        onStart={() => setEditing('password')}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          onPasswordSaved()
          setEditing(null)
        }}
      />
    </dl>
  )
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-3 sm:py-3.5">
      <div className="flex items-start gap-3">
        <dt className="w-24 shrink-0 text-[13px] font-semibold text-ink-500 pt-0.5">
          {label}
        </dt>
        <dd className="flex-1 min-w-0 text-sm text-ink">{value}</dd>
      </div>
    </div>
  )
}

interface RowShellProps {
  label: string
  value?: React.ReactNode
  editing: boolean
  actionLabel: string
  onStart: () => void
  onCancel: () => void
  children?: React.ReactNode
}

function RowShell({
  label,
  value,
  editing,
  actionLabel,
  onStart,
  onCancel,
  children,
}: RowShellProps) {
  return (
    <div className="py-3 sm:py-3.5">
      <div className="flex items-start gap-3">
        <dt className="w-24 shrink-0 text-[13px] font-semibold text-ink-500 pt-0.5">
          {label}
        </dt>
        <dd className="flex-1 min-w-0">
          {editing ? (
            <div className="max-w-sm">{children}</div>
          ) : (
            <div className="text-sm text-ink truncate">{value}</div>
          )}
        </dd>
        <div className="shrink-0">
          {editing ? (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink-500 hover:text-ink"
              aria-label={`Annuller redigering af ${label.toLowerCase()}`}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Annuller
            </button>
          ) : (
            <button
              type="button"
              onClick={onStart}
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink-500 hover:text-ink"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function NameRow({
  value,
  editing,
  onStart,
  onCancel,
  onSaved,
}: {
  value: string
  editing: boolean
  onStart: () => void
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const [name, setName] = useState(value)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editing) {
      setName(value)
      setError(null)
    }
  }, [editing, value])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await updateAccountName(name.trim())
      await onSaved()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setBusy(false)
    }
  }

  return (
    <RowShell
      label="Navn"
      value={value}
      editing={editing}
      actionLabel="Rediger"
      onStart={onStart}
      onCancel={onCancel}
    >
      <form onSubmit={handleSubmit} className={formStackClass}>
        <Field
          id="account-name"
          label="Visningsnavn"
          type="text"
          autoFocus
          value={name}
          onChange={setName}
        />
        {error && <Alert>{error}</Alert>}
        <Submit busy={busy} label="Gem navn" />
      </form>
    </RowShell>
  )
}

function PasswordRow({
  editing,
  onStart,
  onCancel,
  onSaved,
}: {
  editing: boolean
  onStart: () => void
  onCancel: () => void
  onSaved: () => void
}) {
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editing) {
      setCurrentPw('')
      setNewPw('')
      setError(null)
    }
  }, [editing])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await updateAccountPassword(currentPw, newPw)
      onSaved()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setBusy(false)
    }
  }

  return (
    <RowShell
      label="Adgangskode"
      value={<span className="tabular-nums text-ink-500">••••••••••</span>}
      editing={editing}
      actionLabel="Skift"
      onStart={onStart}
      onCancel={onCancel}
    >
      <form onSubmit={handleSubmit} className={formStackClass}>
        <Field
          id="current-pw"
          label="Nuværende adgangskode"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={currentPw}
          onChange={setCurrentPw}
        />
        <Field
          id="new-pw"
          label="Ny adgangskode"
          type="password"
          autoComplete="new-password"
          value={newPw}
          onChange={setNewPw}
        />
        {error && <Alert>{error}</Alert>}
        <Submit busy={busy} label="Skift adgangskode" />
      </form>
    </RowShell>
  )
}

function EmailRow({
  value,
  editing,
  onStart,
  onCancel,
  onSaved,
}: {
  value: string
  editing: boolean
  onStart: () => void
  onCancel: () => void
  onSaved: () => Promise<void>
}) {
  const [step, setStep] = useState<'request' | 'confirm'>('request')
  const [newEmail, setNewEmail] = useState('')
  const [pw, setPw] = useState('')
  const [code, setCode] = useState('')
  const [mailDelivery, setMailDelivery] = useState<'log' | 'smtp' | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editing) {
      setStep('request')
      setNewEmail('')
      setPw('')
      setCode('')
      setMailDelivery(null)
      setEmailError(null)
      setError(null)
    }
  }, [editing])

  function validateNewEmailField(value: string): string | null {
    const result = validateEmail(value)
    return result.ok ? null : result.message
  }

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const fieldErr = validateNewEmailField(newEmail)
    if (fieldErr) {
      setEmailError(fieldErr)
      return
    }
    setBusy(true)
    try {
      const result = validateEmail(newEmail)
      const delivery = await requestEmailChange(
        result.ok ? result.email : newEmail.trim(),
        pw
      )
      setPw('')
      setMailDelivery(delivery)
      setStep('confirm')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setBusy(false)
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await confirmEmailChange(code.trim())
      await onSaved()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Fejl')
    } finally {
      setBusy(false)
    }
  }

  return (
    <RowShell
      label="E-mail"
      value={<span className="tabular-nums">{value}</span>}
      editing={editing}
      actionLabel="Skift"
      onStart={onStart}
      onCancel={onCancel}
    >
      {step === 'request' ? (
        <form onSubmit={handleRequest} className={formStackClass}>
          <p className="text-xs text-ink-500 leading-snug -mt-0.5">
            Vi sender en 6-cifret bekræftelseskode til den nye adresse.
          </p>
          <Field
            id="new-email"
            label="Ny e-mail"
            type="email"
            autoComplete="email"
            autoFocus
            value={newEmail}
            error={emailError}
            onChange={(v) => {
              setNewEmail(v)
              if (emailError) setEmailError(validateNewEmailField(v))
            }}
            onBlur={() => setEmailError(validateNewEmailField(newEmail))}
          />
          <Field
            id="email-pw"
            label="Nuværende adgangskode"
            type="password"
            autoComplete="current-password"
            value={pw}
            onChange={setPw}
          />
          {error && <Alert>{error}</Alert>}
          <Submit busy={busy} label="Send bekræftelseskode" />
        </form>
      ) : (
        <form onSubmit={handleConfirm} className={formStackClass}>
          {mailDelivery === 'log' ? (
            <p
              role="status"
              className="text-xs text-ink-700 bg-paper-200 border border-rule rounded-sm px-2.5 py-1.5 leading-snug"
            >
              Udvikling: der sendes ingen rigtig mail. Find bekræftelseskoden i{' '}
              <span className="font-mono">logs/mail.log</span> i projektmappen. Sæt{' '}
              <span className="font-mono">MAIL_DRIVER=smtp</span> i{' '}
              <span className="font-mono">.env</span> for at sende til{' '}
              <span className="tabular-nums">{newEmail}</span>.
            </p>
          ) : (
            <p className="text-xs text-ink-500 leading-snug">
              Tjek indbakken på{' '}
              <span className="tabular-nums text-ink">{newEmail}</span> og indtast koden.
            </p>
          )}
          <Field
            id="email-code"
            label="Bekræftelseskode (6 cifre)"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={setCode}
          />
          {error && <Alert>{error}</Alert>}
          <div className="flex flex-wrap items-center gap-3 pt-0.5">
            <Submit busy={busy} label="Bekræft e-mail" />
            <button
              type="button"
              onClick={() => {
                setStep('request')
                setError(null)
                setCode('')
              }}
              className="text-xs text-ink-500 hover:text-ink underline-offset-2 hover:underline"
            >
              Brug en anden e-mail
            </button>
          </div>
        </form>
      )}
    </RowShell>
  )
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  onBlur,
  autoComplete,
  inputMode,
  autoFocus,
  error,
}: {
  id: string
  label: string
  type: string
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  autoComplete?: string
  inputMode?: 'numeric' | 'text' | 'email'
  autoFocus?: boolean
  error?: string | null
}) {
  const errorId = error ? `${id}-error` : undefined
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        required
        autoComplete={autoComplete}
        inputMode={inputMode}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        className={`${inputClass}${error ? ' border-signal focus:border-signal focus:ring-signal/15' : ''}`}
      />
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-signal">
          {error}
        </p>
      )}
    </div>
  )
}

function Submit({ busy, label }: { busy: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="bg-ink hover:bg-ink-700 disabled:bg-ink-300 text-paper text-xs font-semibold rounded-md px-3.5 py-1.5 transition-colors rounded-sm"
    >
      {busy ? 'Gemmer…' : label}
    </button>
  )
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      className="text-xs text-signal-900 bg-signal-50 border border-signal-200 rounded-sm px-2.5 py-1.5"
    >
      {children}
    </div>
  )
}

export default AccountSettingsFields
